import logoGreen from "@bodhistreams/ui/img/logo_green.png";
import { jsPDF } from "jspdf";
import { formatCertificateDate, sanitizeFileName } from "./certificateUtils";

// Brand palette (RGB) for the printed certificate.
const PDF_SAGE = [107, 158, 141];
const PDF_GOLD = [201, 168, 118];
const PDF_INK = [38, 48, 43];
const PDF_BODY = [70, 82, 76];
const PDF_MUTE = [126, 136, 130];
const PDF_PAPER = [252, 251, 248];

// Load and cache the brand logo as an <img> so jsPDF can embed it.
let logoImagePromise;
const loadLogoImage = () => {
	if (logoImagePromise) {
		return logoImagePromise;
	}
	logoImagePromise = new Promise((resolve) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => resolve(null);
		img.src = logoGreen;
	});
	return logoImagePromise;
};

// jsPDF measures the 14 standard fonts without the `Tc` letter-spacing it then
// emits, so `align: "center"` combined with `charSpace` renders the text
// off-centre to the right. Centre it ourselves from the tracked width instead.
const centerTracked = (doc, text, x, y, charSpace) => {
	const width = doc.getTextWidth(text) + charSpace * (text.length - 1);
	doc.text(text, x - width / 2, y, { charSpace });
};

export const downloadCertificatePdf = async ({ values, membershipName }) => {
	const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
	const W = doc.internal.pageSize.getWidth(); // ~842
	const H = doc.internal.pageSize.getHeight(); // ~595
	const cx = W / 2;
	const issuedOn = formatCertificateDate(new Date().toISOString());
	const startOn = formatCertificateDate(values.service_start_date);

	// Paper background.
	doc.setFillColor(...PDF_PAPER);
	doc.rect(0, 0, W, H, "F");

	// Double decorative frame.
	doc.setDrawColor(...PDF_SAGE);
	doc.setLineWidth(1.5);
	doc.rect(22, 22, W - 44, H - 44);
	doc.setDrawColor(...PDF_GOLD);
	doc.setLineWidth(0.75);
	doc.rect(30, 30, W - 60, H - 60);

	// Logo, centered at the top.
	let y = 58;
	const logo = await loadLogoImage();
	if (logo?.naturalWidth) {
		const logoH = 42;
		const logoW = logoH * (logo.naturalWidth / logo.naturalHeight);
		doc.addImage(logo, "PNG", cx - logoW / 2, y, logoW, logoH);
		y += logoH + 12;
	} else {
		y += 20;
	}

	// Wordmark.
	doc.setFont("helvetica", "bold");
	doc.setFontSize(11);
	doc.setTextColor(...PDF_SAGE);
	centerTracked(doc, "BODHISTREAMS", cx, y, 3);
	y += 38;

	// Title (serif).
	doc.setFont("times", "bold");
	doc.setFontSize(27);
	doc.setTextColor(...PDF_INK);
	doc.text("Certificate of Service & License of Use", cx, y, {
		align: "center",
	});
	y += 24;

	// Gold divider with a centered diamond.
	const half = 150;
	doc.setDrawColor(...PDF_GOLD);
	doc.setLineWidth(0.8);
	doc.line(cx - half, y, cx - 12, y);
	doc.line(cx + 12, y, cx + half, y);
	doc.setFillColor(...PDF_GOLD);
	doc.triangle(cx - 4, y, cx, y - 4, cx + 4, y, "F");
	doc.triangle(cx - 4, y, cx, y + 4, cx + 4, y, "F");
	y += 28;

	// "This certifies that".
	doc.setFont("times", "italic");
	doc.setFontSize(13);
	doc.setTextColor(...PDF_MUTE);
	doc.text("This certifies that", cx, y, { align: "center" });
	y += 27;

	// Licensee business name.
	doc.setFont("times", "bold");
	doc.setFontSize(23);
	doc.setTextColor(...PDF_INK);
	doc.text(values.business_name, cx, y, { align: "center" });
	y += 24;

	// Address.
	doc.setFont("helvetica", "normal");
	doc.setFontSize(11);
	doc.setTextColor(...PDF_MUTE);
	const addressLines = doc.splitTextToSize(values.property_address, 560);
	doc.text(addressLines, cx, y, { align: "center" });
	y += addressLines.length * 15 + 22;

	// Main certifying statement.
	doc.setFont("helvetica", "normal");
	doc.setFontSize(11.5);
	doc.setTextColor(...PDF_BODY);
	const statement = `is authorized under an active commercial direct license agreement with BodhiStreams to publicly perform and play background music at the location named above, effective ${startOn} and continuing for as long as the subscription remains active.`;
	const statementLines = doc.splitTextToSize(statement, 672);
	doc.text(statementLines, cx, y, {
		align: "center",
		lineHeightFactor: 1.5,
	});

	// Details row — three columns near the lower third.
	const detailY = 350;
	const cols = [
		{ x: cx - 235, label: "Service Start", value: startOn },
		{ x: cx, label: "Authorized Contact", value: values.contact_name },
		{ x: cx + 235, label: "Subscription Plan", value: membershipName },
	];
	doc.setDrawColor(...PDF_GOLD);
	doc.setLineWidth(0.5);
	doc.line(cx - 117, detailY - 14, cx - 117, detailY + 18);
	doc.line(cx + 117, detailY - 14, cx + 117, detailY + 18);
	for (const col of cols) {
		doc.setFont("helvetica", "bold");
		doc.setFontSize(8);
		doc.setTextColor(...PDF_SAGE);
		centerTracked(doc, col.label.toUpperCase(), col.x, detailY, 0.8);
		doc.setFont("helvetica", "normal");
		doc.setFontSize(11);
		doc.setTextColor(...PDF_INK);
		const valueLines = doc.splitTextToSize(col.value, 200);
		doc.text(valueLines, col.x, detailY + 16, { align: "center" });
	}
	if (values.contact_email) {
		doc.setFont("helvetica", "normal");
		doc.setFontSize(8.5);
		doc.setTextColor(...PDF_MUTE);
		doc.text(values.contact_email, cx, detailY + 30, { align: "center" });
	}

	// Legal fine print.
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...PDF_MUTE);
	const fine =
		"BodhiStreams maintains music licensing agreements with its content providers and warrants that it holds the necessary rights to distribute, stream, and publicly perform their music worldwide for in-store commercial background music. All works remain registered with performing rights organizations in relevant territories while eligible for direct licensing through BodhiStreams where applicable.";
	const fineLines = doc.splitTextToSize(fine, 726);
	doc.text(fineLines, cx, 418, { align: "center", lineHeightFactor: 1.45 });

	// Signature block (left).
	const sigX = 70;
	const sigY = 500;
	doc.setFont("times", "bolditalic");
	doc.setFontSize(15);
	doc.setTextColor(...PDF_INK);
	doc.text("BodhiStreams", sigX, sigY - 6);
	doc.setDrawColor(...PDF_INK);
	doc.setLineWidth(0.6);
	doc.line(sigX, sigY, sigX + 190, sigY);
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8.5);
	doc.setTextColor(...PDF_MUTE);
	doc.text(
		"Issued by BodhiStreams · Authorized Representative",
		sigX,
		sigY + 14,
	);
	doc.text(`Issued ${issuedOn}`, sigX, sigY + 27);

	// Seal (right) — concentric rings with the logo as a faint underimpression
	// (watermark) and the legend text crisp on top.
	const sealX = W - 120;
	const sealY = 498;
	doc.setDrawColor(...PDF_SAGE);
	doc.setLineWidth(1.4);
	doc.circle(sealX, sealY, 40);
	doc.setDrawColor(...PDF_GOLD);
	doc.setLineWidth(0.6);
	doc.circle(sealX, sealY, 33);

	// Logo underimpression — drawn faint and centered, behind the text.
	if (logo?.naturalWidth) {
		const markH = 34;
		const markW = markH * (logo.naturalWidth / logo.naturalHeight);
		doc.saveGraphicsState();
		doc.setGState(new doc.GState({ opacity: 0.12 }));
		doc.addImage(
			logo,
			"PNG",
			sealX - markW / 2,
			sealY - markH / 2,
			markW,
			markH,
		);
		doc.restoreGraphicsState();
	}

	// Legend text on top of the watermark.
	doc.setFont("helvetica", "bold");
	doc.setFontSize(7);
	doc.setTextColor(...PDF_SAGE);
	centerTracked(doc, "DIRECT", sealX, sealY - 16, 1);
	centerTracked(doc, "LICENSE", sealX, sealY - 7, 1);
	centerTracked(doc, String(new Date().getFullYear()), sealX, sealY + 24, 1);

	// Footer.
	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...PDF_MUTE);
	centerTracked(doc, "bodhistreams.com", cx, H - 42, 0.5);

	doc.save(
		`${sanitizeFileName(values.business_name)}-bodhistreams-certificate.pdf`,
	);
};
